export const SIM_HZ = 60;
export const STEP = 1 / SIM_HZ;
const MAX_DELTA = 0.1; // tab-switch / hitch clamp: sim slows, never spirals
const MAX_CATCHUP_STEPS = 5;

export interface FrameStats {
  /** Real time since the previous rAF frame — what dropped frames look like. */
  frameMs: number;
  /** CPU time spent inside this frame's step+render work. */
  cpuMs: number;
  simMs: number;
  renderMs: number;
  stepsRun: number;
}

/**
 * Fixed-timestep driver: 60 Hz sim with an accumulator, render every rAF with
 * an interpolation alpha in [0,1). `timescale` feeds slow-mo; `hitstop()` sets
 * timescale to 0 for a real-time duration (render keeps running — impact, not lag).
 * Pixi's ticker must NOT drive the sim; this loop calls render explicitly.
 */
export class GameLoop {
  timescale = 1;
  private acc = 0;
  private last = 0;
  private rafId = 0;
  private running = false;
  private hitstopLeft = 0;

  onFrameStats: ((stats: FrameStats) => void) | null = null;

  constructor(
    private readonly step: (dt: number) => void,
    private readonly render: (alpha: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Freeze the sim for `seconds` of real time (render continues). */
  hitstop(seconds: number): void {
    this.hitstopLeft = Math.max(this.hitstopLeft, seconds);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    const frameStart = performance.now();
    const rawDeltaMs = now - this.last;
    let delta = rawDeltaMs / 1000;
    this.last = now;
    if (delta > MAX_DELTA) delta = MAX_DELTA;

    let scale = this.timescale;
    if (this.hitstopLeft > 0) {
      this.hitstopLeft -= delta;
      scale = 0;
    }
    this.acc += delta * scale;

    let steps = 0;
    const simStart = performance.now();
    while (this.acc >= STEP && steps < MAX_CATCHUP_STEPS) {
      this.step(STEP);
      this.acc -= STEP;
      steps++;
    }
    if (steps === MAX_CATCHUP_STEPS && this.acc >= STEP) this.acc = 0; // drop backlog
    const simEnd = performance.now();

    this.render(Math.min(this.acc / STEP, 1));
    const renderEnd = performance.now();

    this.onFrameStats?.({
      frameMs: rawDeltaMs,
      cpuMs: renderEnd - frameStart,
      simMs: simEnd - simStart,
      renderMs: renderEnd - simEnd,
      stepsRun: steps,
    });
    this.rafId = requestAnimationFrame(this.frame);
  };
}
